// Copyright (c) Ping. All rights reserved.
//
// Smart link unfurl for the composer. The SENDER scrapes a shared URL
// once and ships the resulting og: metadata in a `com.ping.link_preview`
// envelope, so receivers render the card without ever fetching the URL
// (privacy-preserving + instant on their end).
//
// Strategy: prefer OUR OWN backend proxy, then a hosted API, then a bare
// card. The app runs under a strict CSP whose `connect-src` does NOT allow
// arbitrary publisher hosts, so a direct `fetch(targetUrl)` to scrape og:
// tags is ALWAYS blocked — we never attempt it. Order:
//   1) `GET /v1/unfurl` — our SSRF-hardened server-side scraper. Same
//      origin (CSP `'self'`), authed, no third-party data sharing, parses
//      og:/twitter:/<title>. This is the durable, rate-limit-free path.
//   2) microlink — hosted fallback when our backend can't reach the site
//      (or returns nothing useful). JS-rendered pages it can't, microlink
//      sometimes can.
//   3) bare card (the URL itself) so SOMETHING always renders.
// All network calls are time-boxed so a slow unfurl never blocks the send.

import { get } from '@core/network/api';

export interface UnfurledLink {
  readonly url: string;
  readonly title: string;
  readonly description?: string | undefined;
  readonly imageUrl?: string | undefined;
}

// Stop at whitespace and markdown/bracket punctuation so a pasted
// `[label](https://…)` doesn't capture the `](` glue into the URL.
const URL_RE = /\bhttps?:\/\/[^\s<>"'`(){}[\]]+/i;
const UNFURL_TIMEOUT_MS = 6000;

// Bare-domain fallback (no scheme) — `test.com`, `example.co.uk/path`.
// Group 2 is the TLD, checked against a curated set so `Node.js`,
// `file.txt`, `3.14` don't unfurl. Mirrors the composer + tile
// autolinkers so what previews matches what renders as a link.
const BARE_DOMAIN_RE =
  /\b((?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+([a-z]{2,24}))(?:\/[^\s<>"'`(){}[\]]*)?/gi;
const COMMON_TLDS = new Set([
  'com', 'org', 'net', 'io', 'co', 'dev', 'app', 'ai', 'xyz', 'me', 'gg',
  'tv', 'info', 'biz', 'online', 'site', 'tech', 'store', 'blog', 'news',
  'cloud', 'page', 'link', 'live', 'chat', 'edu', 'gov', 'mil', 'uk', 'us',
  'ca', 'de', 'fr', 'es', 'it', 'nl', 'ru', 'jp', 'cn', 'in', 'br', 'au',
  'se', 'no', 'fi', 'dk', 'ch', 'at', 'be', 'pl', 'pt', 'cz', 'gr', 'ie',
  'nz', 'za', 'mx', 'kr', 'sg', 'hk', 'tw', 'ae', 'sa', 'tr', 'ua', 'id',
  'ph', 'my', 'th', 'vn',
]);

/** First linkable URL in a block of text (http(s) or bare domain), or undefined. */
export function extractFirstUrl(text: string): string | undefined {
  const m = URL_RE.exec(text);
  if (m?.[0] !== undefined) return m[0];
  // No scheme URL — look for the first bare domain with a known TLD.
  BARE_DOMAIN_RE.lastIndex = 0;
  let bm: RegExpExecArray | null;
  while ((bm = BARE_DOMAIN_RE.exec(text)) !== null) {
    if (COMMON_TLDS.has((bm[2] ?? '').toLowerCase())) {
      return `https://${bm[0]}`;
    }
  }
  return undefined;
}

function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function withTimeout(ms: number): { signal: AbortSignal; cancel: () => void } {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  return { signal: ctrl.signal, cancel: () => clearTimeout(t) };
}

export async function unfurlLink(url: string): Promise<UnfurledLink> {
  const viaBackend = await tryOwnBackend(url);
  if (viaBackend !== undefined) return viaBackend;
  const viaApi = await tryMicrolink(url);
  if (viaApi !== undefined) return viaApi;
  // Bare fallback — always renders a minimal, useful card. (No direct
  // site fetch: CSP `connect-src` blocks it, so it would only error.)
  return { url, title: domainOf(url) };
}

interface BackendUnfurlResponse {
  url?: string;
  title?: string;
  description?: string;
  image?: string;
}

// Our own `GET /v1/unfurl` proxy (same-origin, authed, SSRF-hardened).
// Returns undefined when the call fails OR yields nothing useful (no
// title AND no image) so the microlink fallback gets a turn. A 401 here
// must never log the user out (`X-Ping-Skip-Reauth`) — a failed preview
// is a no-op, not a session-death signal.
async function tryOwnBackend(url: string): Promise<UnfurledLink | undefined> {
  try {
    const res = await get<BackendUnfurlResponse>(
      `/v1/unfurl?url=${encodeURIComponent(url)}`,
      { headers: { 'X-Ping-Skip-Reauth': '1' } },
    );
    const hasTitle = typeof res.title === 'string' && res.title.length > 0;
    const hasImage = typeof res.image === 'string' && res.image.length > 0;
    if (!hasTitle && !hasImage) return undefined; // let microlink try for richer meta
    return {
      url: res.url ?? url,
      title: hasTitle ? (res.title as string) : domainOf(url),
      ...(res.description !== undefined && res.description.length > 0
        ? { description: res.description }
        : {}),
      ...(hasImage ? { imageUrl: res.image as string } : {}),
    };
  } catch {
    return undefined;
  }
}

interface MicrolinkResponse {
  status?: string;
  data?: {
    title?: string;
    description?: string;
    url?: string;
    image?: { url?: string } | null;
    logo?: { url?: string } | null;
  };
}

async function tryMicrolink(url: string): Promise<UnfurledLink | undefined> {
  const { signal, cancel } = withTimeout(UNFURL_TIMEOUT_MS);
  try {
    const res = await fetch(
      `https://api.microlink.io/?url=${encodeURIComponent(url)}`,
      { signal },
    );
    if (!res.ok) return undefined;
    const json = (await res.json()) as MicrolinkResponse;
    if (json.status !== 'success' || json.data === undefined) return undefined;
    const d = json.data;
    const title = d.title !== undefined && d.title.length > 0 ? d.title : domainOf(url);
    const imageUrl = d.image?.url ?? d.logo?.url ?? undefined;
    return {
      url: d.url ?? url,
      title,
      ...(d.description !== undefined && d.description.length > 0
        ? { description: d.description }
        : {}),
      ...(imageUrl !== undefined ? { imageUrl } : {}),
    };
  } catch {
    return undefined;
  } finally {
    cancel();
  }
}

